import React from 'react';

import {
  BorderColor as BorderColorIcon,
  EditNote as EditAllIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { Chip, IconButton, Typography } from '@mui/material';

import { Nullable } from '@clearverify/types';
import { convertCentsToDollars, formatMoney, parseNullableValue } from '@clearverify/utils';

import { QueryMode, RevenueShareType, StripeAccountStatus } from 'src/api';
import { Tooltip } from 'src/components/common';
import type { TableColumn, TableSelectedRowId } from 'src/components/table';
import {
  Table,
  TableExcelColumn,
  TableFilter,
  TableRowAction,
  TableThemes,
  TableTopAction,
} from 'src/components/table';
import { FilterKeys } from 'src/constants/filter';
import { ModalId } from 'src/constants/modals';
import { useEmployerBillingList } from 'src/hooks/billings/useEmployerBillingList';
import { useFilterStorage, useSearch } from 'src/hooks/common';
import { useEmployersAutocomplete } from 'src/hooks/employer';
import { useModal } from 'src/hooks/shared';

import { ChipStyles } from './constants';

type Billing = Exclude<
  ReturnType<typeof useEmployerBillingList>['employerBillings'],
  undefined
>[number];

export const EmployersBillingTable: React.FC = () => {
  const { employerBillings, employerBillingsListCount, loading } = useEmployerBillingList();

  const employersSelectProps = useEmployersAutocomplete({
    queryFilter: searchText => ({
      filter: { displayName: { contains: searchText, mode: QueryMode.Insensitive } },
    }),
  });

  const { openModal } = useModal();

  const { setSearchValue, searchValue } = useSearch();
  const { getPaginationObjectByKey } = useFilterStorage();
  const pagination = getPaginationObjectByKey(FilterKeys.EmployersBillingTable);

  // biome-ignore lint/correctness/useExhaustiveDependencies: useEffect must not depend on "pagination" here
  React.useEffect(() => {
    pagination.resetPage();
  }, [searchValue]);

  const [selectedIds, setSelectedIds] = React.useState<TableSelectedRowId[]>([]);

  const topActions: Array<TableTopAction> = React.useMemo(
    () => [
      {
        actionName: 'Edit selected employers',
        show: selectedIds.length > 0,
        icon: <EditIcon />,
        onAction: () =>
          openModal(ModalId.EmployerBillingRulesChangeModal, {
            billingRuleIds: selectedIds,
          }),
        render() {
          return (
            <Tooltip title={this.actionName}>
              <IconButton key={this.actionName} onClick={this.onAction} disabled={loading}>
                {this.icon}
              </IconButton>
            </Tooltip>
          );
        },
      },
      {
        actionName: 'Edit all employers',
        icon: <EditAllIcon />,
        onAction: () =>
          openModal(ModalId.EmployerBillingRulesChangeModal, {
            billingRuleIds: [],
            isAllSelected: true,
          }),
        render() {
          return (
            <Tooltip title={this.actionName}>
              <IconButton key={this.actionName} onClick={this.onAction} disabled={loading}>
                {this.icon}
              </IconButton>
            </Tooltip>
          );
        },
      },
    ],
    [loading, openModal, selectedIds],
  );

  const rowActions = React.useMemo(
    (): Array<TableRowAction<Billing>> => [
      {
        startIcon: <BorderColorIcon style={{ margin: '0 10px 0 0' }} />,
        actionName: 'View Details',
        onAction: employerBilling =>
          openModal(ModalId.EmployerBillingRulesChangeModal, {
            billingRuleIds: employerBilling.id,
            revenueShare:
              employerBilling.revenueShareSetting.revenueShareType === 'Flat'
                ? convertCentsToDollars(employerBilling.revenueShareSetting.revenueShareFlat)
                : employerBilling.revenueShareSetting.revenueSharePercents || 0,
            revenueShareType: employerBilling.revenueShareSetting.revenueShareType,
          }),
      },
    ],
    [openModal],
  );

  const filters = React.useMemo(
    (): TableFilter[] => [
      {
        id: 'employer',
        type: 'autocomplete',
        fieldProps: {
          label: 'Employer Name',
          ...employersSelectProps,
          options: employersSelectProps.options.filter(option => !!option.revenueShareSettingId),
        },
      },
    ],
    [employersSelectProps],
  );

  return (
    <Table
      dataSource={employerBillings ?? []}
      rowCount={employerBillingsListCount}
      columns={columns}
      rowActions={rowActions}
      topActions={topActions}
      onSearch={setSearchValue}
      pagination={pagination}
      loading={loading}
      selectedIds={selectedIds}
      setSelectedIds={setSelectedIds}
      selectable
      excelColumns={excelColumns}
      filters={filters}
      tableTheme={TableThemes.withBorder}
    />
  );
};

const columns: Array<TableColumn<Billing>> = [
  {
    title: 'Name',
    key: 'fullName',
    render: (_valueByDataPath, employerBilling) => {
      return (
        <Typography variant="body2">
          {parseNullableValue(employerBilling.revenueShareSetting.employer?.displayName)}
        </Typography>
      );
    },
  },
  {
    title: 'Revenue Share',
    key: 'revenueShare',
    render: (_valueByDataPath, employerBilling) => {
      return (
        <Typography variant="body2">
          {employerBilling.revenueShareSetting.revenueShareType === RevenueShareType.Flat
            ? `${formatMoney(employerBilling.revenueShareSetting.revenueShareFlat, 0)}`
            : `${employerBilling.revenueShareSetting.revenueSharePercents}%`}
        </Typography>
      );
    },
  },
  {
    title: 'Bill. Method Status',
    key: 'billingMethodStatus',
    render: (_valueByDataPath, employerBilling) => {
      return employerBilling.stripeStatus ? (
        <Chip
          label={employerBilling.stripeStatus}
          size="small"
          variant="filled"
          sx={getBillingChipStyles(employerBilling.stripeStatus)}
        />
      ) : (
        '-'
      );
    },
  },
];

const excelColumns: Array<TableExcelColumn<Billing>> = [
  {
    header: 'Name',
    key: 'revenueShareSetting.employer.displayName',
  },
  {
    header: 'Revenue Share',
    key: 'revenueShare',
    render: (_, employerBilling) => {
      return employerBilling.revenueShareSetting.revenueShareType === RevenueShareType.Flat
        ? `${formatMoney(employerBilling.revenueShareSetting.revenueShareFlat, 0)}`
        : `${employerBilling.revenueShareSetting.revenueSharePercents}%`;
    },
  },
  {
    header: 'Bill. Method Status',
    key: 'billingMethodStatus',
    render: (_valueByDataPath, employerBilling) => employerBilling.stripeStatus,
  },
];

const getBillingChipStyles = (stripeStatus?: Nullable<string>) => {
  if (isStripeAccountStatus(stripeStatus)) {
    return ChipStyles[StripeAccountStatus[stripeStatus]];
  }
  return ChipStyles[StripeAccountStatus.NotConnected];
};

const isStripeAccountStatus = (
  stripeStatus: unknown,
): stripeStatus is keyof typeof StripeAccountStatus => {
  return typeof stripeStatus === 'string' && stripeStatus in StripeAccountStatus;
};
